import { getRuntimeConfig } from "../Config/RuntimeConfig";
import { ensureGtag, loadGtagScript, GtagFn } from "./gtagLoader";
// Circular (CookieConsentUtils imports initAnalytics) but only read at call time.
import { getCookiePreferences } from "../Utils/CookieConsentUtils";

// GA4 Web data stream Measurement ID for the standalone "ER-booking-website"
// property (537010482). Non-secret — it ships in the client bundle.
//
// NOTE: This module uses gtag.js directly (NOT the Firebase JS SDK). The target
// is a standalone GA4 web property, not a Firebase project, so gtag.js is the
// correct transport. The filename is kept for import stability.
// For QA/DebugView, use the GA Debugger Chrome extension (no in-app debug flag).
const MEASUREMENT_ID = "G-JK7NQL1JQH";

let _ready = false;
let _initPromise: Promise<boolean> | null = null;

// Calls made before GA4 is up are held here and replayed in order once
// initAnalytics() succeeds. Without this they were dropped: consent is
// re-applied asynchronously on every full page load, so an event fired early
// on that load — notably payment_confirmation on the MPGS callback page — met
// analytics() === null and was lost. Only users who already granted statistic
// consent are queued, so nothing recorded before consent is ever sent later.
export const MAX_PENDING_CALLS = 50;
let _pending: unknown[][] = [];

const enqueue = (...args: unknown[]): void => {
  _pending.push(args);
  if (_pending.length > MAX_PENDING_CALLS) _pending.shift();
};

/**
 * Initialize GA4 (gtag.js) for the ER-booking-website property. Idempotent.
 * MUST only be called after the user has granted `statistic` consent (callers
 * enforce this). Returns false when disabled by the runtime flag or on the
 * server (SSR). `send_page_view:false` keeps only our explicit P0 events.
 */
export async function initAnalytics(): Promise<boolean> {
  if (_ready) return true;
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    try {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return false; // SSR / non-browser
      }
      const config = await getRuntimeConfig();
      if (config?.NEXT_PUBLIC_ENABLE_ANALYTICS !== "true") {
        _pending = [];
        return false;
      }

      // Shared shim + script injection (also used by Floodlight); queued
      // commands flush once gtag.js loads.
      const gtag = ensureGtag();
      loadGtagScript(MEASUREMENT_ID);

      gtag("js", new Date());
      gtag("config", MEASUREMENT_ID, { send_page_view: false });

      _ready = true;
      const queued = _pending;
      _pending = [];
      queued.forEach((args) => gtag(...args));
      return true;
    } catch (error) {
      console.error("Analytics init failed:", error);
      return false;
    } finally {
      // Allow a future retry only if init did not complete.
      if (!_ready) _initPromise = null;
    }
  })();

  return _initPromise;
}

/**
 * The gtag function once initialized (after consent). Before that, for a user
 * whose statistic consent is stored, a queueing stand-in whose calls replay
 * when init succeeds; otherwise null.
 */
export function analytics(): GtagFn | null {
  if (typeof window === "undefined") return null;
  if (!_ready) return getCookiePreferences().statistic ? enqueue : null;
  return (window as unknown as { gtag?: GtagFn }).gtag ?? null;
}
